import { FacilityId } from '@gyym/sdk'
import {
  cachedGyymPrefetch,
  composeServerProps,
  gyymPrefetch,
  withGyymAuth,
  withGyymSSR,
} from '@gyym/sdk-next'

import { SingleColumnLayout } from '@/components/Layouts/legacy/SingleColumnLayout'
import { JsonLd } from '@/components/Seo/JsonLd'
import { FacilityDetailView } from '@/components/Views/FacilityDetailView'
import { buildFacilityJsonLd, PROD_SITE_BASE_URL } from '@/lib/jsonLd'
import { buildOgImageUrl } from '@/lib/ogiGenerator'
import {
  buildFacilityLink,
  FACILITY_INDEX_TITLE,
  formatCityCode,
  getRequestedFacilityPath,
  isCanonicalFacilityPath,
  useCity,
  useFacility,
} from '@/modules/Facilities'
import { getPrefectureById } from '@/modules/Prefectures'

type FacilityDetailProps = {
  facilityId: FacilityId
}

export const getServerSideProps = composeServerProps(
  withGyymAuth(),
  withGyymSSR(async (ctx, client) => {
    const { facilityId } = ctx.query ?? {}

    const [facilityKey, facility] = await gyymPrefetch(
      client.user.facilities.get,
      Number(facilityId),
    )

    if (!facility) {
      return {
        notFound: true,
      }
    }

    // Only the canonical URL of the facility is served
    const requestedPath = getRequestedFacilityPath(
      ctx.req.headers['x-pathname'],
      ctx.query,
    )

    if (!isCanonicalFacilityPath(requestedPath, facility)) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        facilityId: facility.id,
      },
      cache: [
        [facilityKey, facility],
        /**
         * Commented out due to 'large page data' issue
         * @see https://nextjs.org/docs/messages/large-page-data
         */
        // gyymPrefetch(client.user.facilities.listTimetables, Number(facilityId)),
        cachedGyymPrefetch((c) => c.user.prefectures.list),
      ],
    }
  }),
)

/**
 * Facility detail page component
 */
const FacilityDetail = (props: FacilityDetailProps) => {
  const { data } = useFacility(props.facilityId)
  const facility = data!

  const prefecture = getPrefectureById(facility.prefectureId)!
  const city = useCity(`${facility.cityCode}`)!

  // Build canonical URL for JSON-LD
  const facilityLink = buildFacilityLink({
    id: facility.id,
    cityCode: facility.cityCode,
    prefectureId: facility.prefectureId,
  })
  const canonicalUrl = `${PROD_SITE_BASE_URL}${facilityLink.as}`

  // Keywords for SEO meta and JSON-LD
  const keywords = [
    facility.name,
    facility.cityName,
    facility.station,
    'gyym',
    'GYYM',
    'ジム',
    'フィットネス',
    'スタジオ',
    'ジム 都度利用',
    'ジム 体験',
    'ジム 入会金無料',
    'ジム 初心者',
  ]

  // Build JSON-LD structured data
  const jsonLd = buildFacilityJsonLd({
    facility,
    prefecture: {
      name: prefecture.name,
      slug: prefecture.slug,
    },
    city: {
      code: city.code,
      name: city.name,
    },
    canonicalUrl,
    keywords,
  })

  return (
    <SingleColumnLayout
      title={`${facility.name} / ${facility.cityName} / ${facility.station} | GYYM (都度利用サービス)`}
      description={`【フィットネス都度利用サービスGYYM】${facility.name} / ${facility.cityName} / ${facility.station} | 利用料金は利用した分だけ！ジム初心者やしっかり体験したい方も安心の入会金無料・ビジターOKの都度利用型のジムネットワークサービス`}
      keywords={keywords}
      ogImageUrl={buildOgImageUrl`**${facility.name}**`}
      breadcrumbs={[
        { label: 'TOP', href: '/' },
        { label: FACILITY_INDEX_TITLE, href: '/facilities' },
        {
          label: prefecture.name,
          href: {
            pathname: `/facilities/[prefectureSlug]`,
            query: {
              prefectureSlug: prefecture.slug,
            },
          },
          as: `/facilities/${prefecture.slug}`,
        },
        {
          label: city.name,
          href: {
            pathname: `/facilities/[prefectureSlug]/cities/[cityCode]`,
            query: {
              prefectureSlug: prefecture.slug,
              cityCode: city.code,
            },
          },
          as: `/facilities/${prefecture.slug}/C${formatCityCode(city.code)}`,
        },
        { label: facility.name, href: '' },
      ]}
      breadcrumbsGrayBackground
    >
      <JsonLd data={jsonLd} />
      <FacilityDetailView
        facility={facility}
        prevPage={{
          label: prefecture.name,
          href: `/facilities/${prefecture.slug}`,
        }}
        prefecture={{
          name: prefecture.name,
          slug: prefecture.slug,
        }}
        city={{
          code: city.code,
          name: city.name,
        }}
      />
    </SingleColumnLayout>
  )
}

export default FacilityDetail
